import type { Observable } from 'rxjs';

/**
 * How far a schema migration during the open has come.
 *
 * After an update with a schema bump, `addCollections` copies every document of
 * the bumped collections into a new store — ten at a time, RxDB's own choice.
 * On a phone that is about five milliseconds a document, so a bump of the
 * Plana and Profile takes most of a minute. Without this the start screen
 * looked exactly like a hang and reported itself as one.
 *
 * Kept free of RxDB so the start screen can read it without pulling RxDB in.
 */

export interface MigrationProgress {
    /** Documents to copy, over every collection that migrated in this open. */
    total: number;
    handled: number;
    /** `performance.now()` of the last document count that went up. */
    lastProgressAt: number;
}

/** The part of RxDB's migration state this needs. */
interface MigrationStateLike {
    collection: { name: string };
    $: Observable<{
        status: string;
        count: { total: number; handled: number };
    }>;
}

interface DatabaseLike {
    migrationStates(): Observable<MigrationStateLike[]>;
}

interface CollectionCount {
    total: number;
    handled: number;
}

/** The start screen redraws at most this often; RxDB reports every batch. */
const NOTIFY_INTERVAL_MS = 250;

let counts = new Map<string, CollectionCount>();
let progress: MigrationProgress | null = null;
let listeners: (() => void)[] = [];
let notifyTimer: ReturnType<typeof setTimeout> | null = null;

function notify() {
    if (notifyTimer !== null) {
        return;
    }
    notifyTimer = setTimeout(() => {
        notifyTimer = null;
        for (const listener of listeners) {
            listener();
        }
    }, NOTIFY_INTERVAL_MS);
}

function publish() {
    let total = 0;
    let handled = 0;
    for (const count of counts.values()) {
        total += count.total;
        // An interrupted earlier start leaves its count behind, and RxDB goes
        // on adding to it: the count can pass the total.
        handled += Math.min(count.handled, count.total);
    }
    const advanced = progress === null || handled > progress.handled;
    progress = {
        total,
        handled,
        lastProgressAt: advanced
            ? performance.now()
            : (progress?.lastProgressAt ?? performance.now())
    };
    notify();
}

/** Forget any earlier open's migration. */
export function forgetMigrationProgress(): void {
    counts = new Map();
    progress = null;
}

/**
 * Follow the database's migrations until the returned stop is called. Only a
 * collection seen RUNNING counts: a status document left DONE by an earlier
 * version would otherwise add its old total to this open.
 *
 * The progress outlives the stop: a migration that failed belongs in the
 * report filed about it.
 *
 * RxDB announces a migration state from inside its constructor, BEFORE that
 * constructor has set `$`. So each state is followed one microtask later, and
 * nothing here may ever throw: an exception in this observer runs back into
 * RxDB and takes the migration down with it (seen in the browser).
 */
export function watchMigrations(db: DatabaseLike): () => void {
    forgetMigrationProgress();
    let stopped = false;
    const followed = new Set<string>();
    const statusSubscriptions: { unsubscribe(): void }[] = [];

    const follow = (state: MigrationStateLike) => {
        if (stopped || !state.$) {
            return;
        }
        const name = state.collection.name;
        statusSubscriptions.push(
            state.$.subscribe((status) => {
                try {
                    if (status.status !== 'RUNNING' && !counts.has(name)) {
                        return;
                    }
                    counts.set(name, {
                        total: status.count.total,
                        handled: status.count.handled
                    });
                    publish();
                } catch (error) {
                    console.warn('[RxDB] Umstellung nicht lesbar', error);
                }
            })
        );
    };

    const statesSubscription = db.migrationStates().subscribe((states) => {
        for (const state of states) {
            const name = state.collection?.name;
            if (!name || followed.has(name)) {
                continue;
            }
            followed.add(name);
            queueMicrotask(() => {
                try {
                    follow(state);
                } catch (error) {
                    console.warn('[RxDB] Umstellung nicht lesbar', error);
                }
            });
        }
    });
    return () => {
        stopped = true;
        statesSubscription.unsubscribe();
        for (const subscription of statusSubscriptions) {
            subscription.unsubscribe();
        }
        // The end of the migration is progress too: whatever the open does
        // next gets the full notice delay before it counts as stalled.
        if (progress !== null) {
            progress = { ...progress, lastProgressAt: performance.now() };
        }
    };
}

/** `null` while no migration has started in this open. */
export function readMigrationProgress(): MigrationProgress | null {
    return progress;
}

export function subscribeMigrationProgress(listener: () => void): () => void {
    listeners.push(listener);
    return () => {
        listeners = listeners.filter((other) => other !== listener);
    };
}

/**
 * How long the migration has been without progress, or `null` when there was
 * none. A migration that keeps counting is no stall, however long it takes.
 */
export function migrationQuietFor(now = performance.now()): number | null {
    if (progress === null) {
        return null;
    }
    return now - progress.lastProgressAt;
}

/** For the report: `Umstellung: 3200 von 9268 Einträgen, zuletzt vor 2 s` */
export function formatMigrationProgress(
    now = performance.now()
): string | null {
    if (progress === null) {
        return null;
    }
    const quietSeconds = Math.round((now - progress.lastProgressAt) / 1000);
    return `Umstellung: ${progress.handled} von ${progress.total} Einträgen, zuletzt vor ${quietSeconds} s`;
}
